import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, ThemeProvider, Typography } from '@mui/material';
import MUIDataTable from 'mui-datatables';

import { FBoxContainer, FCheckbox, FHSkeletonLoader } from '../../base';
import {
  allClientsViewColumns,
  allClientsViewOptions,
  supervisionMetricsTableTheme,
} from './config';
import { FORTA_BLUE, FORTA_GRAY2, FORTA_WHITE } from '../../../utils/colors';
import { formatSecondsToHHMM2 } from '../../../helpers';

import type { RootState } from '../../../store';

function TableLoader() {
  return (
    <Box>
      {Array.from({ length: 5 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 3,
            borderTop: `1px solid ${FORTA_GRAY2}`,
            backgroundColor: FORTA_WHITE,
          }}
        >
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
        </Box>
      ))}
    </Box>
  );
}

function AllClientsView() {
  const { supervisionMetricsList, loading: metricsLoading } = useSelector(
    (state: RootState) => state.metrics,
  );

  const [hideZeroSessionHourClients, setHideZeroSessionHourClients] = useState<boolean>(false);

  const data = useMemo(() => {
    let list = supervisionMetricsList.map((m) => {
      const {
        client,
        session_duration,
        client_assessment_duration,
        group_parent_supervision_duration,
        client_supervision_duration,
        csn_session_ratio,
        parent_supervision_duration,
      } = m;
      return {
        id: client?.id,
        name: `${client.first_name} ${client.last_name}`,
        session_duration,
        client_supervision_duration,
        client_assessment_duration,
        group_parent_supervision_duration,
        csn_session_ratio,
        parent_supervision_duration,
      };
    });
    if (hideZeroSessionHourClients) {
      list = list.filter((m) => m.session_duration !== 0);
    }
    return list;
  }, [supervisionMetricsList, hideZeroSessionHourClients]);

  const [sessionTotal, csnTotal, ratioTotal, psnTotal, caTotal, gpsnTotal] = useMemo(() => {
    let sessionTotal = 0;
    let csnTotal = 0;
    let ratioTotal = 0;
    let psnTotal = 0;
    let gpsnTotal = 0;
    let caTotal = 0;
    data.forEach((m) => {
      sessionTotal += m.session_duration;
      csnTotal += m.client_supervision_duration;
      ratioTotal += m.csn_session_ratio;
      psnTotal += m.parent_supervision_duration;
      gpsnTotal += m.group_parent_supervision_duration;
      caTotal += m.client_assessment_duration;
    });
    return [sessionTotal, csnTotal, ratioTotal, psnTotal, caTotal, gpsnTotal];
  }, [data, hideZeroSessionHourClients]);

  return (
    <FBoxContainer sx={{ boxShadow: 'none', flexGrow: 0 }}>
      <Box display={'flex'} alignItems={'center'} sx={{ mt: 1, mb: 2 }}>
        <FCheckbox
          sx={{ mr: 0 }}
          iconStyles={{ width: '25px', height: '25px', boxShadow: 'none' }}
          checked={hideZeroSessionHourClients}
          onChange={(e) => setHideZeroSessionHourClients(e.target.checked)}
        />
        <Typography sx={{ mr: 0.5, color: FORTA_BLUE.primary, fontWeight: 700 }}>
          Hide clients with zero session(53) hours
        </Typography>
      </Box>
      <Box>
        {metricsLoading ? (
          <TableLoader />
        ) : (
          <ThemeProvider theme={supervisionMetricsTableTheme}>
            <MUIDataTable
              title=""
              data={data}
              columns={allClientsViewColumns}
              options={allClientsViewOptions}
            />
          </ThemeProvider>
        )}
        {supervisionMetricsList?.length > 0 && (
          <Box
            sx={{
              backgroundColor: FORTA_GRAY2,
              borderRadius: '4px',
              padding: '10px',
              textAlign: 'left',
            }}
          >
            <Grid container sx={{ fontWeight: 700 }}>
              <Grid item xs={1.92}>
                Total
              </Grid>
              <Grid item xs={1.68}>
                <Box>{formatSecondsToHHMM2(sessionTotal)}</Box>
              </Grid>
              <Grid item xs={1.68}>
                <Box>{formatSecondsToHHMM2(csnTotal)}</Box>
              </Grid>
              <Grid item xs={1.74}>
                <Box>{`${((ratioTotal * 100) / (data?.length === 0 ? 1 : data.length)).toFixed(
                  2,
                )}% (Avg)`}</Box>
              </Grid>
              <Grid item xs={1.7}>
                <Box>{formatSecondsToHHMM2(psnTotal)}</Box>
              </Grid>
              <Grid item xs={1.68}>
                <Box>{formatSecondsToHHMM2(caTotal)}</Box>
              </Grid>
              <Grid item xs={1.6}>
                <Box>{formatSecondsToHHMM2(gpsnTotal)}</Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </FBoxContainer>
  );
}

export default AllClientsView;
